<template>
  <Drawer :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <div class="modal-header">
      <div class="modal-title">Completar solicitação</div>
      <button
        type="button"
        class="modal-close"
        aria-label="Fechar"
        @click="$emit('update:modelValue', false)"
      >
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="modal-body">
      <section class="modal-section">
        <div class="field">
          <label for="convertClientName">Nome da cliente</label>
          <input
            id="convertClientName"
            v-model="clientNameModel"
            type="text"
            placeholder="Nome"
            :aria-invalid="!!clientNameError"
          />
          <p v-if="clientNameError" class="field-error" role="alert">{{ clientNameError }}</p>
        </div>
        <div class="field">
          <label for="convertClientPhone">Telefone</label>
          <PhoneInput
            id="convertClientPhone"
            v-model="clientPhoneModel"
            placeholder="(11) 99999-9999"
            aria-label="Telefone"
            :invalid="!!clientPhoneError"
            aria-describedby="convertClientPhone-error"
          />
          <p v-if="clientPhoneError" id="convertClientPhone-error" class="field-error" role="alert">{{ clientPhoneError }}</p>
          <p v-else-if="existingClient" class="helper-text" role="status">
            Cliente existente: {{ existingClient.name }}
          </p>
        </div>
      </section>

      <section class="modal-section">
        <div class="field">
          <label for="convertDate">Data</label>
          <input id="convertDate" type="date" v-model="dateModel" :aria-invalid="!!dateError" />
          <p v-if="dateError" class="field-error" role="alert">{{ dateError }}</p>
        </div>
        <div class="time-inputs">
          <div class="field">
            <label for="convertStartTime">Horário início</label>
            <input id="convertStartTime" type="time" v-model="startTimeModel" :aria-invalid="!!startTimeError" />
            <p v-if="startTimeError" class="field-error" role="alert">{{ startTimeError }}</p>
          </div>
          <div class="field">
            <label for="convertEndTime">Horário término</label>
            <input id="convertEndTime" type="time" v-model="endTimeModel" :aria-invalid="!!endTimeError" />
            <p v-if="endTimeError" class="field-error" role="alert">{{ endTimeError }}</p>
          </div>
        </div>
      </section>

      <section v-if="userIsAdmin" class="modal-section">
        <div class="field">
          <label for="convertLash">Lash</label>
          <v-select
            id="convertLash"
            :options="allLashes"
            label="name"
            v-model="lashModel"
            placeholder="Selecione a lash"
            :class="{ 'field-invalid': lashError }"
          />
          <p v-if="lashError" class="field-error" role="alert">{{ lashError }}</p>
        </div>
      </section>

      <section class="modal-section">
        <h4 class="section-title">Serviços</h4>
        <div class="service-select-row">
          <v-select
            :options="allServices"
            label="name"
            v-model="selectedServiceForAdd"
            placeholder="Adicionar serviço"
            @option:selected="onServiceSelected"
          />
        </div>
        <div v-if="selectedServices && selectedServices.length" class="selected-services-list">
          <div v-for="sel in selectedServices" :key="sel.service" class="selected-service-row">
            <div class="selected-service-info">
              <div class="selected-service-name">{{ serviceName(sel.service) }}</div>
            </div>
            <div class="selected-service-actions">
              <CurrencyInput
                :model-value="sel.price"
                @update:model-value="sel.price = $event"
                class="price-input"
                aria-label="Preço"
              />
              <button type="button" class="btn-icon btn-icon-danger" aria-label="Remover" @click="$emit('delete-service', sel.service)">
                <i class="material-icons">delete_outline</i>
              </button>
            </div>
          </div>
        </div>
        <p v-if="servicesError" class="field-error" role="alert">{{ servicesError }}</p>
        <p v-else-if="!selectedServices || !selectedServices.length" class="helper-text">
            Nenhum serviço adicionado. Adicione ao menos um serviço.
          </p>
      </section>

      <section class="modal-section">
        <div class="field">
          <h4 class="section-title">Observações</h4>
          <textarea id="convertComments" v-model="commentsModel" class="comments-input" rows="3" placeholder="Anote qualquer observação importante sobre o atendimento"></textarea>
        </div>
      </section>

      <AppointmentPaymentFields
        :paid="paid"
        :payment-method="paymentMethod"
        :all-payment-methods="allPaymentMethods"
        :total="total"
        :default-fee-percent="defaultFeePercent"
        @update:paid="$emit('update:paid', $event)"
        @update:payment-method="$emit('update:paymentMethod', $event)"
      />
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" :disabled="saving" @click="$emit('update:modelValue', false)">
        Cancelar
      </button>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSave">
        {{ saving ? 'Salvando…' : 'Criar atendimento' }}
      </button>
    </div>
  </Drawer>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import AppointmentPaymentFields from '@/components/AppointmentPaymentFields.vue';
import vSelect from 'vue-select';

export default {
  name: 'ConvertRequestModal',
  components: { Drawer, CurrencyInput, PhoneInput, AppointmentPaymentFields, vSelect },
  props: {
    modelValue: { type: Boolean, default: false },
    request: { type: Object, default: null },
    startDate: { type: String, default: '' },
    endDate: { type: String, default: '' },
    clientName: { type: String, default: '' },
    clientPhone: { type: String, default: '' },
    /** When phone matches an existing client: { id, name }. From clientStore lookup only. */
    existingClient: { type: Object, default: null },
    selectedServices: { type: Array, default: () => [] },
    paid: { type: [String, Number, null], default: null },
    paymentMethod: { type: [Object, null], default: null },
    allPaymentMethods: { type: Array, default: () => [] },
    defaultFeePercent: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    comments: { type: String, default: '' },
    lash: { type: [Object, String], default: '' },
    allServices: { type: Array, default: () => [] },
    allLashes: { type: Array, default: () => [] },
    userIsAdmin: { type: Boolean, default: false },
    serviceName: { type: Function, required: true },
    saving: { type: Boolean, default: false },
  },
  emits: [
    'update:modelValue',
    'update:clientName',
    'update:clientPhone',
    'update:startDate',
    'update:endDate',
    'update:selectedServices',
    'update:paid',
    'update:paymentMethod',
    'update:comments',
    'update:lash',
    'add-service',
    'delete-service',
    'save',
  ],
  data() {
    return {
      clientNameError: '',
      clientPhoneError: '',
      dateError: '',
      startTimeError: '',
      endTimeError: '',
      lashError: '',
      servicesError: '',
      selectedServiceForAdd: null,
    };
  },
  computed: {
    clientNameModel: {
      get() { return this.clientName; },
      set(v) { this.$emit('update:clientName', v); },
    },
    clientPhoneModel: {
      get() { return this.clientPhone; },
      set(v) { this.$emit('update:clientPhone', v); },
    },
    dateModel: {
      get() { return this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10) : ''; },
      set(v) {
        const time = this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16) : '08:00';
        this.$emit('update:startDate', v ? v + 'T' + time + ':00' : '');
      },
    },
    startTimeModel: {
      get() { return this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16) : ''; },
      set(v) {
        const date = this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10) : '';
        this.$emit('update:startDate', date ? date + 'T' + (v || '08:00') + ':00' : '');
      },
    },
    endTimeModel: {
      get() { return this.endDate && this.endDate.length >= 16 ? this.endDate.slice(11, 16) : ''; },
      set(v) {
        const date = this.endDate && this.endDate.length >= 10 ? this.endDate.slice(0, 10) : '';
        this.$emit('update:endDate', date ? date + 'T' + (v || '09:00') + ':00' : '');
      },
    },
    commentsModel: {
      get() { return this.comments; },
      set(v) { this.$emit('update:comments', v); },
    },
    lashModel: {
      get() { return this.lash; },
      set(v) { this.$emit('update:lash', v); },
    },
    selectedServiceModel: {
      get() { return ''; },
      set(v) { if (v) this.$emit('update:selectedService', v); },
    },
  },
  watch: {
    request: {
      handler(req) {
        if (req && req.lash && this.allLashes && this.allLashes.length) {
          const lashObj = this.allLashes.find((u) => u.id === req.lash);
          if (lashObj) this.$emit('update:lash', { ...lashObj });
        }
      },
      immediate: true,
    },
  },
  methods: {
    onServiceSelected(service) {
      if (service) {
        this.$emit('add-service', service);
        this.selectedServiceForAdd = null;
      }
    },
    validate() {
      this.clientNameError = '';
      this.clientPhoneError = '';
      this.dateError = '';
      this.startTimeError = '';
      this.endTimeError = '';
      this.lashError = '';
      this.servicesError = '';
      let valid = true;
      if (!this.clientName || !this.clientName.trim()) {
        this.clientNameError = 'Informe o nome da cliente.';
        valid = false;
      }
      if (!this.clientPhone || !this.clientPhone.trim()) {
        this.clientPhoneError = 'Informe o telefone.';
        valid = false;
      }
      const dateStr = this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10) : '';
      if (!dateStr) {
        this.dateError = 'Selecione a data.';
        valid = false;
      }
      const startTime = this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16) : '';
      if (!startTime) {
        this.startTimeError = 'Selecione o horário de início.';
        valid = false;
      }
      const endTime = this.endDate && this.endDate.length >= 16 ? this.endDate.slice(11, 16) : '';
      if (!endTime) {
        this.endTimeError = 'Selecione o horário de término.';
        valid = false;
      }
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.endTimeError = 'O horário de término deve ser posterior ao de início.';
        valid = false;
      }
      if (this.userIsAdmin && (!this.lash || !this.lash.id)) {
        this.lashError = 'Selecione a lash.';
        valid = false;
      }
      if (!this.selectedServices || !this.selectedServices.length) {
        this.servicesError = 'Adicione ao menos um serviço.';
        valid = false;
      }
      return valid;
    },
    handleSave() {
      if (this.saving || !this.validate()) return;
      this.$emit('save');
    },
  },
};
</script>

<style scoped>
.modal-body {
  flex: 1;
  min-height: 0;
}
.modal-section {
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--color-border);
}
.modal-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.field {
  margin-bottom: var(--space-md);
}

.field:last-child {
  margin-bottom: 0;
}

.time-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-sm);
}

.service-select-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.service-select-row .v-select {
  flex: 1;
  min-width: 0;
}

.btn-add-service {
  flex-shrink: 0;
  background: var(--color-accent);
  color: #fff;
}

.btn-add-service:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.btn-add-service .material-icons {
  font-size: 18px;
}

.selected-services-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.selected-service-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  border: 1px solid var(--color-border);
}

.selected-service-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text);
}

.selected-service-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.selected-service-actions .btn-icon {
  color: var(--color-text-secondary);
}

.selected-service-actions .btn-icon:hover {
  color: var(--color-error);
  background: var(--color-error-bg);
}

.price-input {
  width: 100px;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

.field-invalid :deep(.v-select .vs__dropdown-toggle) {
  border-color: var(--color-error);
}

.helper-text {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-md);
  margin-top: var(--space-xs);
}

.totals-column {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  flex: 1 1 120px;
}

.amount {
  font-weight: 600;
  color: var(--color-text);
}

.comments-input {
  min-height: 72px;
}
</style>
